import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MapPin, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

/* Locations (sites) management — admins AND supervisors can define the sites
   staff must pick from when clocking in/out. */
export default function AdminLocationsPage() {
  const { t } = useLang();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/locations")).data,
  });
  const items = data?.items || [];

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["locations"] });

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/locations", { name: name.trim() });
      toast.success(t("locationCreated"));
      setAddOpen(false);
      setName("");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (loc, v) => {
    try {
      await api.patch(`/locations/${loc.id}`, { isActive: v });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await api.delete(`/locations/${deleteTarget.id}`);
      toast.success(t("locationDeleted"));
      setDeleteTarget(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{t("locations")}</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">{t("locationsSubtitle")}</p>
          </div>
          <Button onClick={() => setAddOpen(true)} data-testid="add-location-button">
            <Plus className="mr-2 h-4 w-4" /> {t("addLocation")}
          </Button>
        </div>

        {isLoading ? (
          <Skeleton className="h-48 w-full rounded-xl" />
        ) : items.length === 0 ? (
          <div data-testid="locations-empty" className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border p-10 text-center">
            <MapPin className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t("noLocations")}</p>
            <Button variant="outline" onClick={() => setAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> {t("addLocation")}
            </Button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <Table data-testid="locations-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("locationName")}</TableHead>
                  <TableHead className="w-28">{t("locActive")}</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((loc) => (
                  <TableRow key={loc.id} data-testid="location-row">
                    <TableCell className="font-medium">
                      <span className="inline-flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-primary" /> {loc.name}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Switch
                        data-testid="location-active-switch"
                        checked={loc.isActive}
                        onCheckedChange={(v) => toggleActive(loc, v)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button size="icon" variant="ghost" data-testid="location-delete-button"
                        onClick={() => setDeleteTarget(loc)}>
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("addLocation")}</DialogTitle>
          </DialogHeader>
          <Input
            data-testid="location-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("locationName")}
            maxLength={120}
            onKeyDown={(e) => e.key === "Enter" && create()}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="location-save-button" onClick={create} disabled={busy || !name.trim()}>
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteTarget !== null} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteLocationTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.name} — {t("deleteLocationNote")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="location-delete-confirm" onClick={remove} disabled={busy}>
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
